import { concat, merge, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import LoadingStatus from 'enums/loadingStatus';
import { selectMapLibraryStatus } from 'layout/redux/selectors';
import siteMapResource$ from './observables';
import { getCommunitySiteMap$ } from '../apis/getCommunitySiteMap';
import processCommunityMap from '../services/processCommunityMap';

/**
 * Factory that returns a redux-observable epic scoped to a specific community.
 *
 * Community id is captured via closure because it lives in plugin host state
 * (`context.state.community`), not in the Redux store.
 *
 * Triggers on `mapLibraryFetch` so the HTTP request races alongside the Maps SDK
 * download rather than waiting for it to finish.
 *
 * On mobile the feature is lazy-loaded (injected after a button click), so
 * `mapLibraryFetch` may have already been dispatched before this epic is registered.
 * To handle that case the epic checks `state$` at registration time: if the map
 * library is already loading or loaded it synthesizes an immediate trigger, avoiding
 * a race where the action is missed entirely.
 *
 * take(1) on the merged trigger below: this epic is registered once per page (via
 * registerEpicOnce, in renderCommunitySiteMap.plugin.js#init — which itself re-runs on every
 * mobile-modal reopen, but the epic instance underneath doesn't). `mapLibraryFetch` re-dispatches
 * on every reopen (the map library plugin re-injects its script tag each bootstrap), so without
 * this the site-map API call — and, transitively, the lot-inventory call chained off
 * fetchSiteMapSuccess in fetchLotInventoryEpic.js — refired on every reopen even though the
 * community's data never changes mid-session. state.lots/housePlans/housingUnits stay from the
 * first fetch; siteMapResource$ being a ReplaySubject(1) (see redux/observables.js) means the map
 * overlay still redraws correctly from that cached resource when the plugin remounts.
 *
 * @param {object} params
 * @param {number} params.communityId
 * @param {object} params.dataActions - Actions from the feature data slice instance
 * @param {object} params.mapLibraryActions - Actions from the layout mapLibrary slice instance
 * @returns {import('redux-observable').Epic}
 */
function createFetchCommunitySiteMapEpic({ communityId, dataActions, mapLibraryActions }) {
  return (action$, state$) => {
    const future$ = action$.pipe(ofType(mapLibraryActions.mapLibraryFetch.type));

    // If the epic is registered after mapLibraryFetch was already dispatched
    // (e.g. mobile lazy-load), synthesize an immediate trigger from current state.
    const past$ = state$.pipe(
      take(1),
      filter((state) => selectMapLibraryStatus(state) !== LoadingStatus.Idle.toString()),
      map(() => mapLibraryActions.mapLibraryFetch()),
    );

    return merge(past$, future$).pipe(
      take(1),
      switchMap(() =>
        concat(
          of(dataActions.fetchSiteMap()),
          getCommunitySiteMap$(communityId).pipe(
            map(processCommunityMap),
            filter(Boolean),
            tap((resource) => siteMapResource$.next(resource)),
            map((resource) => dataActions.fetchSiteMapSuccess(resource)),
          ),
        ).pipe(
          catchError((err) => of(dataActions.fetchSiteMapError(err.message))),
        ),
      ),
    );
  };
}

export default createFetchCommunitySiteMapEpic;
